import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
} from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getOutofPolicy } from 'utils/apiRequests';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import {
	defaultColDefination,
	initialGridData,
} from 'CategoryManagement/Explore/DetailedAnalysis/utils';
import {
	getHeaderByTaxonomyType,
	getRequiredCustomFilterObjectFormat,
} from '../../CategoryManagement/utils';

const OutofPolicySpendContext = createContext();

const spendViaText = 'spend via';
const vendorCountText = 'vendors';

export const OutofPolicySpendProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [analysis, setAnalysis] = useState(
		`0 ${spendViaText} 0 ${vendorCountText}`
	);
	const [gridData, setGridData] = useState(initialGridData);
	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const { isSaving, selectedOption, loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	const vendorCount = selectedOption.length;

	const taxonomyHeader = useMemo(() => getHeaderByTaxonomyType(taxonomyType), [
		taxonomyType,
	]);

	const columnDefs = [
		{ headerName: 'Vendor Name', field: 'vendoR_NAME' },
		{ headerName: 'Total Spend', field: 'totalSpend' },
		{ headerName: `${taxonomyHeader} L1Category`, field: 'l1Category' },
		{ headerName: `${taxonomyHeader} L2Category`, field: 'l2Category' },
		{ headerName: `${taxonomyHeader} L3Category`, field: 'l3Category' },
		{ headerName: `${taxonomyHeader} L4Category`, field: 'l4Category' },
		{ headerName: 'Business Unit', field: 'purchasinG_GROUP' },
		{ headerName: 'Country', field: 'country' },
	];

	useEffect(
		() => {
			if (!isSaving && !isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payLoad = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						vendorNames: selectedOption,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payLoad.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					if (vendorCount === 0) {
						setAnalysis(`0 spend via 0 vendors.`);
						setIsLoading(false);
					} else {
						getOutofPolicy(payLoad)
							.then(response => {
								if (response.succeeded) {
									if (response.data === null) {
										setAnalysis('Vendor not present in selected category.');
										setIsLoading(false);
										setGridData(initialGridData);
										return;
									} else {
										if (response.data.length > 0) {
											const sum = response.data.reduce((acc, obj) => {
												return acc + obj.totalSpend;
											}, 0);
											const count = [
												...new Set(response.data.map(x => x.vendoR_NAME)),
											].length;
											const analysisString = `${formatSpendWithDecimalAndSymbol(
												sum,
												currencySymbol,
												hideDecimals,
												2
											).toUpperCase()} ${spendViaText} ${count} ${vendorCountText}`;
											const rowResult = response.data.map(x => {
												return {
													...x,
													totalSpend: formatSpendWithDecimalAndSymbol(
														x.totalSpend,
														currencySymbol,
														hideDecimals,
														2
													).toUpperCase(),
												};
											});
											setAnalysis(analysisString);
											setGridData(prevState => ({
												...prevState,
												rowData: rowResult,
												columnDefs: columnDefs,
												defaultColDef: defaultColDefination,
											}));
											setIsLoading(false);
										} else {
											setAnalysis('Vendor not present in selected category.');
										}
									}
								} else {
									setIsLoading(false);
								}
							})
							.catch(() => setIsLoading(false));
					}
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			isSaving,
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	return (
		<OutofPolicySpendContext.Provider value={{ isLoading, analysis, gridData }}>
			{children}
		</OutofPolicySpendContext.Provider>
	);
};

export const useOutofPolicySpendContext = () => {
	return useContext(OutofPolicySpendContext);
};
